import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PlanStatusBadge } from "@/components/ai/PlanStatusBadge";
import { PlanChangeSummaryList } from "@/components/ai/PlanChangeSummaryList";
import { ActionPlanRecommendationItem } from "@/components/ai/ActionPlanRecommendationItem";
import { DraftMessagePreview } from "@/components/ai/DraftMessagePreview";

// Module-level constant (not recreated every render) for the Accordion's
// uncontrolled default open sections.
const DEFAULT_OPEN_ACCORDION_SECTIONS = ["recommendations", "drafts"];

/**
 * v1.3 Wave 2 — the "Review Plan" expanded view: full detail behind
 * progressive disclosure so the compact chat card stays scannable.
 * Approving from here never executes directly either — it hands off to
 * the same hard-stop confirmation dialog as the compact card.
 */
export const ActionPlanReviewDialog = ({ open, onOpenChange, plan, onApproveClick, canAct }) => {
  const recommendations = plan.recommendations || [];
  const draftMessages = plan.draft_messages || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] sm:max-w-lg" data-testid="ai-action-plan-review-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle>{plan.title}</DialogTitle>
            <PlanStatusBadge status={plan.status} />
          </div>
          <DialogDescription>{plan.summary}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-2">
          <div className="space-y-4">
            {Object.keys(plan.changes_preview || {}).length > 0 && (
              <PlanChangeSummaryList counts={plan.changes_preview} testIdPrefix="ai-action-plan-review-summary" />
            )}

            <Accordion type="multiple" defaultValue={DEFAULT_OPEN_ACCORDION_SECTIONS}>
              {recommendations.length > 0 && (
                <AccordionItem value="recommendations">
                  <AccordionTrigger data-testid="ai-action-plan-review-recommendations-toggle">
                    Affected lessons & coverage ({recommendations.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    {recommendations.map((rec, idx) => (
                      <ActionPlanRecommendationItem key={rec.appointment_id || idx} recommendation={rec} />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              )}
              {draftMessages.length > 0 && (
                <AccordionItem value="drafts">
                  <AccordionTrigger data-testid="ai-action-plan-review-drafts-toggle">
                    Draft messages ({draftMessages.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    {draftMessages.map((msg, idx) => (
                      <DraftMessagePreview
                        key={`${msg.recipient_user_id || "recipient"}-${msg.appointment_id || msg.title || idx}`}
                        message={msg}
                      />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>

            {recommendations.length === 0 && draftMessages.length === 0 && (
              <p className="text-sm text-muted-foreground">No further details for this plan.</p>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="sticky bottom-0 -mx-6 mt-4 border-t bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ai-action-plan-review-close-button">
            Close
          </Button>
          {canAct && (
            <Button
              className="bg-[hsl(var(--role-accent))] hover:opacity-95"
              onClick={onApproveClick}
              data-testid="ai-action-plan-review-approve-button"
            >
              Approve & Execute
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
